import { useState } from 'react'
import { useLocations, usePropertyTypes } from '../hooks/locations'
import { LoadingButton } from '@mui/lab'
import CircularProgress from '@mui/material/CircularProgress'
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbar,
  GridColDef,
  GridValueGetterParams,
  GridRowId,
  GridToolbarContainer,
  GridColumnVisibilityModel,
  GridRenderCellParams,
} from '@mui/x-data-grid'
import Button from '@mui/material/Button'
import api from 'balkerne-core/api'
import { useHistory } from 'react-router-dom'
import { ClimateAnalyticsPermissions, PermissionGate, PropertyPermissions, usePermissions } from 'balkerne-core/roles'
import siteMap from '../siteMap'
import {
  Box,
  Stack,
  useTheme,
  Chip,
  Tooltip,
  ButtonGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from '@mui/material'
import type { MenuProps } from '@mui/material/Menu'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import CheckIcon from '@mui/icons-material/Check'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { useMutation } from 'react-query' // v3

/** Color helper for the chip */
const typeChipColor = (
  isSystem?: boolean,
  label?: string,
): 'default' | 'info' | 'success' | 'warning' | 'secondary' => {
  if (isSystem) return 'info'
  if ((label || '').toLowerCase().includes('unknown')) return 'warning'
  return 'success'
}

async function updatePropertyType(propertyId: string, typeId: string) {
  return api.post(`/locations/${propertyId}/type`, { type_id: typeId })
}

async function createPropertyType(label: string) {
  return api.post(`/locations/types`, { data: { label } })
}

type PropertyListRow = {
  id: GridRowId
  name?: string | null
  property_customer_reference?: string | null
  address?: {
    street?: string | null
    town?: string | null
    postcode?: string | null
  } | null
  type?: {
    id?: string
    label?: string
    is_system?: boolean
  } | null
}

function formatPropertyAddress(address?: PropertyListRow['address']) {
  if (!address) return ''
  return [address.street, address.town, address.postcode].filter(Boolean).join(', ')
}

/** Cell: single-click chip -> dropdown -> mutate -> refetch (plus "Create new type…") */
function TypeCell({
  params,
  propertyTypes,
  onChanged,
  onTypesChanged,
  disabled,
}: {
  params: GridRenderCellParams<any>
  propertyTypes: Array<{ id: string; label: string; is_system?: boolean }> | undefined
  onChanged: () => void // refetch locations
  onTypesChanged: () => void // refetch property types
  disabled?: boolean
}) {
  const currentType = params.row?.type
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newLabel, setNewLabel] = useState('')

  const assignMutation = useMutation(({ propertyId, typeId }: { propertyId: string; typeId: string }) =>
    updatePropertyType(propertyId, typeId),
  )
  const createMutation = useMutation((label: string) => createPropertyType(label))

  const saving = assignMutation.isLoading || createMutation.isLoading

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    if (!disabled && propertyTypes?.length !== undefined) setAnchorEl(e.currentTarget)
  }

  const handleMenuClose: MenuProps['onClose'] = event => {
    // @ts-expect-error may not always be MouseEvent
    event?.stopPropagation?.()
    setAnchorEl(null)
  }

  const handleSelect = async (typeId: string) => {
    if (!typeId || typeId === currentType?.id) {
      setAnchorEl(null)
      return
    }
    await assignMutation.mutateAsync({ propertyId: params.row.id, typeId })
    setAnchorEl(null)
    onChanged() // refetch locations
  }

  const handleOpenCreate = () => {
    setAnchorEl(null)
    setNewLabel('')
    setCreateOpen(true)
  }

  const handleCreate = async () => {
    const label = newLabel.trim()
    if (!label) return
    const res = await createMutation.mutateAsync(label)
    // assume API returns created type { id, label, ... } in res.data
    const created = (res as any)?.data
    await onTypesChanged() // refresh type options
    if (created?.id) {
      await assignMutation.mutateAsync({ propertyId: params.row.id, typeId: created.id })
      onChanged() // refresh row data
    }
    setCreateOpen(false)
  }

  const chipLabel = currentType?.label ?? '—'
  const chipColor = typeChipColor(currentType?.is_system, chipLabel)

  return (
    <>
      <Tooltip title={disabled ? 'Types loading…' : 'Change type'} arrow>
        <Chip
          size="small"
          variant="outlined"
          color={chipColor}
          clickable
          onClick={handleOpen}
          onMouseDown={e => e.stopPropagation()}
          onDoubleClick={e => e.stopPropagation()}
          label={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {chipLabel}
              {saving ? (
                <span className="MuiCircularProgress-root" style={{ display: 'inline-flex' }}>
                  <CircularProgress size={14} />
                </span>
              ) : (
                <ArrowDropDownIcon fontSize="small" />
              )}
            </span>
          }
          sx={{ cursor: disabled ? 'not-allowed' : 'pointer', '&:hover': { opacity: disabled ? 1 : 0.9 } }}
        />
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: -4, horizontal: 'left' }}
        MenuListProps={{ dense: true, onMouseDown: e => e.stopPropagation() }}>
        {(propertyTypes ?? []).map(t => (
          <MenuItem
            key={t.id}
            selected={t.id === currentType?.id}
            disabled={saving}
            onClick={e => {
              e.stopPropagation()
              handleSelect(t.id)
            }}>
            <ListItemIcon>{t.id === currentType?.id ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            {t.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          disabled={saving}
          onClick={e => {
            e.stopPropagation()
            handleOpenCreate()
          }}>
          <ListItemIcon>
            <AddRoundedIcon fontSize="small" />
          </ListItemIcon>
          Create new type
        </MenuItem>
      </Menu>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onMouseDown={e => e.stopPropagation()}
        fullWidth
        maxWidth="xs">
        <DialogTitle>New property type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Label"
            fullWidth
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleCreate()
              }
              e.stopPropagation()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={!newLabel.trim() || saving}>
            {createMutation.isLoading ? <CircularProgress size={16} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const PropertyListToolbar = () => {
  const history = useHistory()
  const [newPropertyMenuAnchor, setNewPropertyMenuAnchor] = useState<null | HTMLElement>(null)

  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between' }}>
      <Stack direction="row" gap={1}>
        <PermissionGate action={PropertyPermissions.Add}>
          <>
            <ButtonGroup size="small" variant="contained" aria-label="new property split button">
              <Button
                onClick={() => history.push(siteMap.AddProperty.path)}
                startIcon={<AddRoundedIcon fontSize="small" />}>
                New Property
              </Button>
              <Button
                size="small"
                aria-label="new property menu"
                onClick={e => {
                  e.stopPropagation()
                  setNewPropertyMenuAnchor(e.currentTarget)
                }}>
                <ArrowDropDownIcon fontSize="small" />
              </Button>
            </ButtonGroup>
            <Menu
              anchorEl={newPropertyMenuAnchor}
              open={Boolean(newPropertyMenuAnchor)}
              onClose={e => {
                // @ts-expect-error may not always be MouseEvent
                e?.stopPropagation?.()
                setNewPropertyMenuAnchor(null)
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              MenuListProps={{ dense: true, onMouseDown: e => e.stopPropagation() }}>
              <MenuItem
                onClick={e => {
                  e.stopPropagation()
                  setNewPropertyMenuAnchor(null)
                  history.push(siteMap.TemplateAddProperties.path)
                }}>
                <ListItemIcon>
                  <UploadFileOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Import from Template (Preview)
              </MenuItem>
            </Menu>
          </>
        </PermissionGate>
        <PermissionGate action={PropertyPermissions.Add}>
          <Button size="small" onClick={() => history.push(siteMap.PropertyGroups.path)} variant="outlined">
            Manage Groups
          </Button>
        </PermissionGate>
      </Stack>
      <GridToolbar />
    </GridToolbarContainer>
  )
}

const PropertyList = () => {
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations, isLoading, isError, refetch, isRefetching } = useLocations({ group_id: groupId })
  const { data: propertyTypes, isLoading: isPropertyTypesLoading, refetch: refetchTypes } = usePropertyTypes()
  const { possible } = usePermissions()
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme()
  const [pendingDeleteProperty, setPendingDeleteProperty] = useState<PropertyListRow | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleRedirect = (propertyId: GridRowId) => {
    history.push(siteMap.Property.getPath(propertyId))
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 250,
      editable: false,
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 1,
      minWidth: 250,
      editable: false,
      valueGetter: (params: GridValueGetterParams) =>
        [params.row?.address?.street, params.row?.address?.town].filter(Boolean).join(', '),
    },
    {
      field: 'postcode',
      headerName: 'Postcode',
      width: 100,
      editable: false,
      valueGetter: (params: GridValueGetterParams) => params.row?.address?.postcode,
    },
    /** Type column (single click + create) */
    possible(ClimateAnalyticsPermissions.Modify)
      ? {
          field: 'type',
          headerName: 'Type',
          minWidth: 200,
          valueGetter: (params: GridValueGetterParams) => params.row?.type?.label ?? '',
          renderCell: (params: GridRenderCellParams) => (
            <TypeCell
              params={params}
              propertyTypes={propertyTypes}
              onChanged={refetch}
              onTypesChanged={refetchTypes}
              disabled={isPropertyTypesLoading}
            />
          ),
        }
      : null,
    {
      field: 'property_customer_reference',
      headerName: 'Reference',
      minWidth: 300,
      flex: 1,
      editable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      getActions: params => {
        const actions: JSX.Element[] = [
          <GridActionsCellItem
            key="view"
            icon={<VisibilityOutlinedIcon fontSize="small" />}
            label="View property"
            onClick={() => handleRedirect(params.row.id)}
            showInMenu
          />,
        ]

        if (possible(PropertyPermissions.Assign)) {
          actions.push(
            <GridActionsCellItem
              key="assign-users"
              icon={<GroupAddOutlinedIcon fontSize="small" />}
              label="Assign users"
              onClick={() => history.push(siteMap.AssignPropertyUsers.getPath(params.row.id))}
              showInMenu
            />,
          )
        }

        if (possible(PropertyPermissions.Edit)) {
          actions.push(
            <GridActionsCellItem
              key="edit"
              icon={<EditOutlinedIcon fontSize="small" />}
              label="Edit property"
              onClick={() => history.push(siteMap.EditProperty.getPath(params.row.id))}
              showInMenu
            />,
          )
        }

        if (possible(PropertyPermissions.Delete)) {
          actions.push(
            <GridActionsCellItem
              key="delete"
              icon={<DeleteForeverIcon fontSize="small" />}
              label="Delete property"
              onClick={() => setPendingDeleteProperty(params.row as PropertyListRow)}
              showInMenu
            />,
          )
        }

        return actions
      },
    },
  ].filter(Boolean) as GridColDef[]

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    name: true,
    address: true,
    postcode: true,
    type: true,
    property_customer_reference: true,
  })

  const handleCloseDeleteDialog = () => {
    if (isDeleting) return
    setPendingDeleteProperty(null)
  }

  const handleDeleteProperty = async () => {
    if (!pendingDeleteProperty?.id) return

    setIsDeleting(true)

    try {
      await api.delete(`/locations/${pendingDeleteProperty.id}`)
      await refetch()
      setPendingDeleteProperty(null)
      enqueueSnackbar('Property deleted', { variant: 'success' })
    } catch (err: any) {
      console.error(err)
      enqueueSnackbar(err?.response?.data?.detail || err?.message || 'Failed to delete property', {
        variant: 'error',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const propertyAddress = formatPropertyAddress(pendingDeleteProperty?.address)
  const propertyReference = pendingDeleteProperty?.property_customer_reference?.trim()
  const propertyName = pendingDeleteProperty?.name?.trim() || 'Unnamed property'

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          alignContent: 'center',
          height: '100%',
        }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <Box height={800} mb={6}>
      {locations && (
        <DataGrid
          components={{ Toolbar: PropertyListToolbar as any }}
          sx={{ backgroundColor: theme.palette.background.default }}
          rows={locations ?? []}
          loading={isLoading || isRefetching}
          columns={columns}
          disableColumnSelector={false}
          onRowDoubleClick={property => handleRedirect(property.id)}
          density="compact"
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={model => setColumnVisibilityModel(model)}
          disableSelectionOnClick
        />
      )}
      <Dialog open={Boolean(pendingDeleteProperty)} onClose={handleCloseDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Delete property?</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {propertyName}
          </Typography>
          {propertyReference && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Reference: {propertyReference}
            </Typography>
          )}
          {propertyAddress && (
            <Typography variant="body2" color="text.secondary">
              Address: {propertyAddress}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <LoadingButton onClick={handleDeleteProperty} variant="contained" color="error" loading={isDeleting}>
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
      {isError && (
        <Box
          sx={{
            top: '25%',
            left: '35%',
            padding: '20px',
            background: 'rgb(209, 95, 95)',
            borderRadius: '5px',
            width: '30%',
            position: 'fixed',
            zIndex: 999999,
          }}>
          Loading error
        </Box>
      )}
    </Box>
  )
}

export default PropertyList
